import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface CreateTimeframeDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: { name: string; description?: string; sectionType: 'timeframe' }) => void;
}

export function CreateTimeframeDialog({ open, onClose, onCreate }: CreateTimeframeDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      onClose();
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    onCreate({
      name: trimmedName,
      description: description.trim() || undefined,
      sectionType: 'timeframe',
    });

    setName('');
    setDescription('');
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Timeframe</DialogTitle>
          <DialogDescription>
            Add a timeframe to group sections by time period (e.g., This Week, Q2 2026).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="timeframe-name">Name</Label>
            <Input
              id="timeframe-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., This Week, Q2 2026, Sprint 5"
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="timeframe-description">Description (optional)</Label>
            <Textarea
              id="timeframe-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this timeframe for?"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
